<svelte:options customElement={{tag: "ia-selfmanagementofmedicationform", props:{id:{reflect:false,type:"String"}}}}/>

<script>
    import {safeHTML} from "@intellectability/platform-common/utils";
    import {getAbortSignal, onMount} from "svelte";
    import {fade} from "svelte/transition";
    import dressing from "@intellectability/platform-common";
    import {Router} from "@intellectability/platform-common/router";
    import {
        CommonStyles,
        LazyLoader,
        MainPreloader,
        Toast,
        VerifyUserAction,
        WipeCache
    } from "@intellectability/platform-common/sv";
    import {
        getApplicationConfig,
        isLoggedIn,
        setCompilationContext,
        urlParams
    } from "@intellectability/platform-common/sv/state";
    import routerConfig from "../router.js";

    // params other than action
    let pageParams = $derived.by(function() {
        let params = urlParams.toObject();
        delete params.action;
        return params;
    });

    let styleLoading = $state(true);
    let env_type = $derived(getApplicationConfig("node_env") || "");

    onMount(function() {
        setCompilationContext($host()?.shadowRoot ?? document);
    });

    /*
     * Self-executing function that automatically performs developer login
     * when the application is running on localhost
     */
    $effect(function() {

        if ($isLoggedIn) {
            return;
        }

        let signal = getAbortSignal();
        dressing.api.do_dev_login({
            signal
        }).then(({success}) => {
            if (!success === true) {
                console.log("Internal login failed");
            }
        }).catch((error) => {
            if (!signal.aborted) {
                console.log(`Internal login failed: ${error.message}`);
            }
        });
    });
</script>

{#if env_type}
    {@render manifest()}
    <!-- sets up the <link> elements for the component to import the common styles -->
    <CommonStyles
            env={env_type}
            onload={() => styleLoading = false}
            onerror={() => styleLoading = false}
    />
    <main class="wa-theme-matter wa-palette-mild wa-brand-purple mb-4">
        {#if styleLoading}
            <!-- show a preload spinner while styles are loading to prevent "FOUC" -->
            <MainPreloader></MainPreloader>
        {:else}
            <!-- render the router component with the imported local config for this stitch -->
            {#if $isLoggedIn}
                {let pageToRender = $derived(routerConfig?.routes[0]?.render)}
                <!--
                    The presence of props implies that the stitch could be a sub page and not visible
                    on load. We render the page ourselves, ignore router configuration and pass in
                    any props in the snippet
                -->
                {let hasParams = $derived(Object.keys(pageParams).length > 0)}
                {#if hasParams && pageToRender}
                    <LazyLoader>
                        {@render asyncLoadPage(pageToRender)}
                    </LazyLoader>
                {:else}
                    <!-- Use the default router if this is at the page level  -->
                    <Router {routerConfig} isLoggedIn={$isLoggedIn}></Router>
                {/if}
                <Toast/>
                <VerifyUserAction/>
                <WipeCache showButton></WipeCache>
            {/if}
        {/if}
    </main>
{/if}

{#snippet asyncLoadPage(
    /** @type {() => Promise<{default:import("svelte").Component<any, any, any>}>} */
    lazyComp)}
    {#await lazyComp()}
        <MainPreloader/>
    {:then {default: Page}}
        <div in:fade|global={{duration: 450}}>
            <Page {...pageParams}/>
        </div>
    {:catch error}
        {@debug error}
        <div>
            Error loading this page
        </div>
    {/await}
{/snippet}

{#snippet manifest()}
    {@html `<script id="stitch-manifest" type="application/json">${await safeHTML.sanitize(JSON.stringify({
        "catastaVersion": __CATASTA_VERSION__,
        "compileDate": __COMPILE_DATE__,
        "stitchName": __STITCH_NAME__
    }))}</script>`}
{/snippet}